import {Injectable} from "@angular/core";

import * as Log from "~/app/core/log";
import * as _ from "lodash";
import {Storage} from "@ionic/storage-angular";
import {BehaviorSubject, Observable} from "rxjs";
import {Directory, Encoding, Filesystem} from "@capacitor/filesystem";
import {filter} from "rxjs/operators";

@Injectable()
export class DataStorageService {
    private localStorage;
    private _waitForReady: BehaviorSubject<boolean>;

    private storageMode: "filesystem"|"storage";

    constructor(
        private readonly storage: Storage
    ) {
        this.storageMode = "filesystem";
        this._waitForReady = new BehaviorSubject<boolean>(false);
        this.init();
    }

    isReady(): boolean {
        return this._waitForReady.value;
    }

    waitForReady(): Promise<void> {
        if (this.isReady()) {
            return Promise.resolve();
        } else {
            return new Promise(resolve => {
                this._waitForReady.subscribe(ready => {
                    if (ready) {
                        resolve();
                    }
                });
            });
        }
    }

    private init() {
        // If using, define drivers here: await this.storage.defineDriver(/*...*/);
        const mode = this.storageMode;
        const waitForReady = this._waitForReady;
        const storage = this.storage;
        let localStorageData: {[key: string]: any} = {};
        let localStorageTimeout = null;

        const internalSaveData = function() {
            if (mode === "storage") {
                storage.set("localStorage.db", JSON.stringify((localStorageData)));
            } else if (mode === "filesystem") {
                Filesystem.writeFile({path: "localStorage.db", directory: Directory.Data, encoding: Encoding.UTF8, data: JSON.stringify((localStorageData))}).then((result) => {
                }).catch((error) => {
                });
            }
        };

        const saveData = function()  {
            if (localStorageTimeout !== null) {
                clearTimeout(localStorageTimeout);
            }
            localStorageTimeout = setTimeout(internalSaveData, 250);
        };

        const loadData = function() {
            if (mode === "storage") {
                storage.get("localStorage.db").then((result) => {
                    if (!result) {
                        localStorageData = {};
                    } else {
                        localStorageData = JSON.parse(result);
                    }
                    waitForReady.next(true);
                });
            } else if (mode === "filesystem") {
                Filesystem.readFile({path: "localStorage.db", directory: Directory.Data, encoding: Encoding.UTF8 }).then((textData) => {
                    Log.out("Reading localStorage.db...");
                    const data = JSON.parse(textData.data);
                    localStorageData = data;
                    waitForReady.next(true);
                }, (error) => {
                    Log.error(Log.Tag.StorageService, error);
                    Log.out("Unable to read localStorage.db, starting fresh data.");
                    localStorageData = {};
                    waitForReady.next(true);
                }).catch((error) => {
                    Log.error(Log.Tag.StorageService, error);
                    Log.out("Unable to read localStorage.db, starting fresh data.");
                    localStorageData = {};
                    waitForReady.next(true);
                });
            }
        };

        loadData();

        this.localStorage = {
            export: () => {
                return JSON.stringify(localStorageData);
            },
            import: (json) => {
                const object = JSON.parse(json);
                for (const [key, value] of Object.entries(object)) {
                    this.store(key, value);
                }
            },
            getItem: (name) => {
                if (!name) return null;
                if (localStorageData.hasOwnProperty(name)) {
                    return localStorageData[name];
                }
                return null;
            },
            key: (id) => {
                const keys = Object.keys(localStorageData);
                if (id >= keys.length) { return null; }
                return keys[id];
            },
            setItemObject: (name: string, value) => {
                if (!name) throw new Error("no id");
                localStorageData[name] = value;
                saveData();
            },
            searchItems: (filter: (item) => boolean) => {
                const result = [];
                for (const key in localStorageData) {
                    const obj = localStorageData[key];
                    if (filter(obj)) result.push(obj);
                }
                return result;
            },
            // Revamp this to be "String" only
            // https://github.com/NathanaelA/nativescript-localstorage/issues/17
            setItem: (name: string, value) => {
                if (value == null) {
                    if (value === null) {
                        localStorageData[name] = "null";
                    } else {
                        localStorageData[name] = "undefined";
                    }
                } else {
                    localStorageData[name] = value.toString();
                }
                saveData();
            },
            removeItem: (name) => {
                if (localStorageData[name]) {
                    delete localStorageData[name];
                    saveData();
                }
            },
            clear: () => {
                localStorageData = {};
                saveData();
            }
        };
        Object.defineProperty(this.localStorage, "length", {
            get: function() {
                return (Object.keys(localStorageData).length);
            },
            enumerable: true,
            configurable: true
        });
    }

    // --------------------------------------------------

    store(key: string, value: any): void {
        if (value) {
            this.localStorage.setItemObject(key, value);
        } else {
            this.localStorage.removeItem(key);
        }
    }

    load(key: string): any {
        const item = this.localStorage.getItem(key);
        return _.cloneDeep(item);
    }

    search(filter: (item) => boolean): Array<any> {
        const items = this.localStorage.searchItems(filter);
        const cloneItems = [];
        items.forEach(i => {
            cloneItems.push(_.cloneDeep(i));
        });
        return cloneItems;
    }

    export(): string {
        return this.localStorage.export();
    }

    import(json: string): void {
        return this.localStorage.import(json);
    }

    clear(): void {
        this.localStorage.clear();
    }
}
